import Styles from './Uploads.module.scss'
import { useImagePickerContext } from '../ImagePickerContext'
import { useStateRef } from 'hooks/useStateRef'
import { useDropzone, FileRejection } from 'react-dropzone'
import { VisuallyHidden } from '@react-aria/visually-hidden'
import { Dropzone } from './Dropzone'
import { RecentUploads } from './RecentUploads'
import { useModals } from 'almanac-ui/Modal'
import { AllUploads } from './AllUploads'
import { showErrorMessageToast } from 'common/MessageToasts/ErrorMessageToast/ErrorMessageToast'
import { ButtonVariant } from 'blaze-ui/Button/Types'
import cx from 'classnames'
import { HeadingLevel } from 'blaze-ui/Heading/Heading'
import { TextVariant } from 'blaze-ui/Text/Text'
import { SelectedImage } from 'types'

interface Props {
  wrapperClassName?: string
  dropzoneClassName?: string
  seeAllButtonVariant?: ButtonVariant
  recentUploadsLabel?: string
  headingLevel?: HeadingLevel
  dropzoneButtonVariant?: ButtonVariant
  dropzoneButtonLabel?: string
  dropzoneLabelVariant?: TextVariant
  dropzoneLabel?: string
  newStack?: boolean
  showSelected?: boolean
  customImages?: SelectedImage[]
  onSelectImageFromAll?: (images: SelectedImage[]) => void
}

const Uploads = ({
  wrapperClassName,
  dropzoneClassName,
  seeAllButtonVariant = 'tertiary',
  recentUploadsLabel,
  headingLevel,
  dropzoneButtonVariant,
  dropzoneButtonLabel,
  dropzoneLabelVariant,
  dropzoneLabel,
  newStack = false,
  showSelected = false,
  customImages = [],
  onSelectImageFromAll,
}: Props) => {
  const imagePickerContext = useImagePickerContext()
  const { uploadImages, isUploading, onConfirm, maxCount, accept, onUploadStart } = imagePickerContext
  const { openModal } = useModals()
  const [fileInputRef, setFileInputRef] = useStateRef<HTMLInputElement | null>(null)
  const { closeStack } = useModals()

  const handleOnChange = async (files: File[]) => {
    await onUploadStart?.(files)
    const uploadedImages = await uploadImages(files, imagePickerContext)

    onConfirm(uploadedImages, imagePickerContext)
  }

  const handleOpenAll = () => {
    openModal(
      AllUploads,
      {
        ...imagePickerContext,
        accept,
        onConfirm: (images) => {
          onConfirm(images, imagePickerContext)
          onSelectImageFromAll?.(images)
          closeStack()
        },
      },
      {
        newStack,
      },
    )
  }

  const handleDropRejected = (fileRejections: FileRejection[]) => {
    if (fileRejections.some(({ errors }) => errors.some(({ code }) => code === 'file-invalid-type'))) {
      showErrorMessageToast('File type not supported')
    } else {
      showErrorMessageToast('An error occurred while uploading files')
    }
  }

  const { getRootProps, isDragActive } = useDropzone({
    accept,
    onDropRejected: handleDropRejected,
    onDropAccepted: handleOnChange,
    disabled: isUploading,
  })

  return (
    <div className={cx(Styles.uploads, wrapperClassName)} {...getRootProps()} data-testid="uploads-provider">
      <Dropzone
        onPress={() => fileInputRef?.click()}
        isDragActive={isDragActive}
        accept={accept}
        className={dropzoneClassName}
        buttonVariant={dropzoneButtonVariant}
        buttonLabel={dropzoneButtonLabel}
        labelVariant={dropzoneLabelVariant}
        label={dropzoneLabel}
      />
      <RecentUploads
        onOpenAll={handleOpenAll}
        seeAllButtonVariant={seeAllButtonVariant}
        headingLevel={headingLevel}
        label={recentUploadsLabel}
        showSelected={showSelected}
        customImages={customImages}
      />

      <VisuallyHidden>
        <input
          type="file"
          accept={accept}
          onChange={(e) => {
            const files = Array.from(e.target.files || [])
            handleOnChange(files)
          }}
          ref={setFileInputRef}
          multiple={maxCount > 1}
          name="insert image"
          id="insert image"
          data-testid="file-input"
        />
      </VisuallyHidden>
    </div>
  )
}

export { Uploads }
